import { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";

import { ConciliationCase } from "@/@types/conciliation-history";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConciliationItemTypeEnum } from "@/constants/conciliation-history";
import {
  useGetDivergenceCaseQuery,
  useGetNsuCaseQuery,
} from "@/services/conciliation-history";

import { useJustificationNames } from "../../hooks/use-justification-names";
import {
  CaseQueryState,
  ConciliationCaseOperationGroup,
  JustificationNames,
} from "../../types";
import {
  groupCaseEvents,
  newestActionFirst,
} from "../../utils/group-case-events";
import { CASE_TIMELINE_LABELS } from "../../utils/messages";
import { CaseApprovalsSummary } from "./case-approvals-summary";
import { CaseOperationGroup } from "./case-operation-group";
import { CaseRelatedItems } from "./case-related-items";
import {
  CASE_TIMELINE_TEST_ID,
  CASE_TIMELINE_LOADING_TEST_ID,
  CASE_TIMELINE_BACK_TEST_ID,
} from "../../utils/case-history-test-ids";

interface CaseTimelineModalProps {
  itemType: ConciliationItemTypeEnum;
  itemId: number;
}

interface CaseTimelineContentProps {
  conciliationCase: ConciliationCase;
  openItemId: number;
  onOpenCase: (itemId: number) => void;
}

function CaseTimelineContent({
  conciliationCase,
  openItemId,
  onOpenCase,
}: CaseTimelineContentProps): JSX.Element {
  const justificationNames: JustificationNames = useJustificationNames(
    conciliationCase.justifications
  );

  const groups: ConciliationCaseOperationGroup[] = newestActionFirst(
    groupCaseEvents(conciliationCase.events)
  );

  if (groups.length === 0 && conciliationCase.approvals.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground p-4 text-center"
        data-testid={CASE_TIMELINE_TEST_ID}
      >
        {CASE_TIMELINE_LABELS.EMPTY}
      </p>
    );
  }

  return (
    <ScrollArea className="h-96 w-full p-1 pr-3" data-testid={CASE_TIMELINE_TEST_ID}>
      <CaseApprovalsSummary approvals={conciliationCase.approvals} />

      <ol className="space-y-3">
        {groups.map((group: ConciliationCaseOperationGroup) => (
          <CaseOperationGroup
            key={group.operation_id}
            group={group}
            justificationNames={justificationNames}
            onOpenCase={onOpenCase}
          />
        ))}
      </ol>

      <CaseRelatedItems
        items={conciliationCase.related_items}
        currentItemId={openItemId}
        onOpenCase={onOpenCase}
      />
    </ScrollArea>
  );
}

interface CaseTimelineBodyProps {
  query: CaseQueryState;
  openItemId: number;
  onOpenCase: (itemId: number) => void;
}

function CaseTimelineBody({
  query,
  openItemId,
  onOpenCase,
}: CaseTimelineBodyProps): JSX.Element {
  if (query.isError) {
    return (
      <p
        className="text-sm text-destructive p-4 text-center"
        data-testid={CASE_TIMELINE_TEST_ID}
      >
        {CASE_TIMELINE_LABELS.LOAD_ERROR}
      </p>
    );
  }

  if (!query.currentData) {
    return (
      <div
        className="flex items-center justify-center py-8"
        role="status"
        aria-label={CASE_TIMELINE_LABELS.LOADING}
        data-testid={CASE_TIMELINE_LOADING_TEST_ID}
      >
        <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <CaseTimelineContent
      conciliationCase={query.currentData}
      openItemId={openItemId}
      onOpenCase={onOpenCase}
    />
  );
}

export function CaseTimelineModal({
  itemType,
  itemId,
}: CaseTimelineModalProps): JSX.Element {
  const [openItemId, setOpenItemId] = useState<number>(itemId);

  const queries: Record<ConciliationItemTypeEnum, CaseQueryState> = {
    [ConciliationItemTypeEnum.DIVERGENCE]: useGetDivergenceCaseQuery(
      { divergenceId: openItemId },
      { skip: itemType !== ConciliationItemTypeEnum.DIVERGENCE }
    ),
    [ConciliationItemTypeEnum.NSU]: useGetNsuCaseQuery(
      { nsuId: openItemId },
      { skip: itemType !== ConciliationItemTypeEnum.NSU }
    ),
  };

  const isAwayFromOrigin: boolean = openItemId !== itemId;

  return (
    <div>
      {isAwayFromOrigin && (
        <Button
          type="button"
          variant="link"
          className="mb-2 h-auto gap-1 p-0 text-[#613F4F]"
          data-testid={CASE_TIMELINE_BACK_TEST_ID}
          onClick={(): void => setOpenItemId(itemId)}
        >
          <ArrowLeft className="h-4 w-4" />
          {CASE_TIMELINE_LABELS.BACK_TO_ORIGIN}
        </Button>
      )}

      <CaseTimelineBody
        query={queries[itemType]}
        openItemId={openItemId}
        onOpenCase={setOpenItemId}
      />
    </div>
  );
}
